import React from 'react';

import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  Box,
  Typography,
  Button,
  Divider,
  Tooltip,
  IconButton,
} from '@mui/material';
import _ from 'lodash';
import { Link, useNavigate } from 'react-router-dom';

import { SheetCellMenuOptions } from '../../../admin/constants/SheetCellMenuOptions';
import {
  FlorencePAGES,
  getPageWithID,
} from '../../../common/components/tfa-auth/constants/pages';
import { SheetCode } from '../../../common/types/Codes';
import { SaveSheet } from '../../../common/types/SaveSheet';
import { displayDateString } from '../../../common/utils/DateUtils';
import { getSheetName } from '../../utils/getSheetName';
import StatusLabel from '../StatusLabel/StatusLabel';

import SheetCellPopupMenu from './SheetCellPopupMenu';

import './SavedSheetCell.scss';

type Props = {
  savedSheet: SaveSheet;
  type: 'admin' | 'florence';
  renameSavedSheet?: (savedSheet: SaveSheet) => void;
  deleteSavedSheet?: (savedSheet: SaveSheet) => void;
};

type SavedSheetGroup = {
  mmText: string; // - Format example: (Modified - 32; Multiplied - 0)
  codes: SheetCode[]; // - Format example: 92544; 92544
};

const modifierText = (modifiers: string[], multiplier: number) => {
  if (_.isEmpty(modifiers) && multiplier <= 1) return '';
  if (_.isEmpty(modifiers)) return '';
  const multStr = multiplier <= 1 ? ')' : ` Units - ${multiplier})`;
  const modStr = modifiers.length > 1 ? 'Modifiers' : 'Modifier';
  return (
    '(' +
    modStr +
    ' - ' +
    modifiers.join('; ') +
    `${multiplier <= 1 ? '' : '; '}` +
    multStr
  );
};

const SavedSheetCell: React.FC<Props> = ({
  savedSheet,
  type,
  renameSavedSheet,
  deleteSavedSheet,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const navigate = useNavigate();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectedItem = (item: SheetCellMenuOptions) => {
    switch (item) {
      case SheetCellMenuOptions.Rename:
        renameSavedSheet?.(savedSheet);
        break;
      case SheetCellMenuOptions.Delete:
        deleteSavedSheet?.(savedSheet);
        break;
    }
    handleClose();
  };

  const savedSheetGroups: SavedSheetGroup[] = React.useMemo(
    () =>
      _.chain(savedSheet.sheetCodes)
        .groupBy(
          (sheetCode) =>
            `${modifierText(sheetCode.modifiers, sheetCode.multiplier)}`
        )
        .map((codes, mmText) => {
          return { mmText, codes };
        })
        .value(),
    [savedSheet.sheetCodes]
  );

  const CellCaption = (group: SavedSheetGroup, index: number) => {
    const codes = group.codes.map((c) => c.code);
    const tooltip = group.codes.map((c) => c.description).join('. \n') + '.';
    return (
      <Tooltip
        key={index}
        enterDelay={500}
        disableFocusListener
        disableTouchListener
        title={<span style={{ whiteSpace: 'pre-line' }}>{tooltip}</span>}
      >
        <div className='sheet-code-wrapper'>
          <Typography
            fontFamily='SFProDisplay-Bold'
            color='primary.main'
            className='sheet-code'
          >
            {codes.join('; ')}{' '}
          </Typography>
          <Typography
            fontFamily='SFProDisplay-Bold'
            color='textFlorence.main'
            className='sheet-code'
          >
            {group.mmText}
            {index === savedSheetGroups.length - 1 ? '' : ';'}{' '}
          </Typography>
        </div>
      </Tooltip>
    );
  };

  const getURL = React.useCallback(() => {
    return getPageWithID(FlorencePAGES.savedSheet, savedSheet.sheetId);
  }, [savedSheet]);

  const onClickOpenNote = () => {
    navigate(getURL());
  };

  return (
    <Box className='table-view-cell-container-white'>
      <Box className='row' sx={{ mb: '7px', justifyContent: 'space-between' }}>
        <Box className='row sheet-title-wrapper'>
          <Link
            color='#002145D9'
            className='sheet-title'
            to={getURL()}
            onClick={() => onClickOpenNote()}
          >
            {getSheetName(savedSheet)}
          </Link>
          <StatusLabel status={savedSheet.status} />
        </Box>
        {type === 'florence' && (
          <IconButton onClick={handleClick}>
            <MoreHorizIcon />
          </IconButton>
        )}
      </Box>
      <Divider />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: '15px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Box className='sheet-bottom-wrapper'>
            <Typography variant='body2' color='textFlorence.main'>
              {savedSheet.createdAt
                ? displayDateString(savedSheet.createdAt, true)
                : '01/01/2000'}
            </Typography>
            <Divider flexItem sx={{ mx: '15px' }} orientation='vertical' />
            {savedSheetGroups.map(CellCaption)}
          </Box>
        </Box>
        {type === 'florence' && (
          <Button
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            variant='text'
            color='primary'
            onClick={() => onClickOpenNote()}
          >
            See details
          </Button>
        )}
      </Box>
      {type === 'florence' && (
        <SheetCellPopupMenu
          handleSelectedItem={handleSelectedItem}
          anchorEl={anchorEl}
          handleClose={handleClose}
          open={open}
          id={id}
        />
      )}
    </Box>
  );
};

export default SavedSheetCell;
